import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import {
  FiArrowLeft,
  FiUser,
  FiFlag,
  FiMessageSquare,
  FiClock,
} from 'react-icons/fi';

import firebase from '@/config/firebase.config';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { useTeamMemberDetailsForSelect } from '@/hooks/useTeamMemberDetailsForSelect';
import { appRoutes } from '@/routes/routes';
import { addNotification, notificationStates } from '@/components/Notification';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Input } from '@/components/Input';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { SingleDatePicker } from '@/components/SingleDatePicker';
import { Button, buttonVariants } from '@/components/Button';
import { SplitButton } from '@/components/SplitButton';
import { TabBar, tabBarVariants } from '@/components/TabBar';
import { EmptyState } from '@/components/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner.component';

import {
  STATUS_OPTIONS,
  PRIORITY_OPTIONS,
  PRIORITY_ARROW,
  PRIORITY_ARROW_COLOR,
} from './TaskDetail.constants';
import { etaToPickerDate, pickerDateToEta } from './TaskDetail.utils';
import { FirebaseImageUploadPlugin } from './UploadPlugin';
import { CommentThread } from './components/CommentThread.component';
import { HistoryPanel } from './components/HistoryPanel.component';
import { AttachmentsSection } from './components/AttachmentsSection.component';
import styles from './TaskDetail.module.scss';

const database = firebase.firestore();

const EMPTY_FORM = {
  title: '',
  description: '',
  assigneeId: null,
  priority: null,
  status: STATUS_OPTIONS[0].value,
  eta: null,
  estimatedTime: '',
  projectId: null,
};

const TaskDetail = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const routeParams = useParams();
  const taskId = routeParams?.taskId || null;
  const isEditMode = Boolean(taskId);

  const profile = useSelector((s) => s.auth.profile);
  const activeCompany = profile?.activeCompany;
  const currentUser = useMemo(
    () => ({ id: profile?.id, name: profile?.name, email: profile?.email }),
    [profile]
  );

  const teams = useSelector((s) => ({
    projects: s.team.projects,
    selectedTeamId: s.team.selectedTeamID,
    active: s.team.active,
    loadingProject: s.team.loadingProject,
  }));
  const activeTeamId =
    teams.active?.activeTeamid ?? teams.selectedTeamId ?? null;
  const { memberDetails, isLoading: membersLoading } =
    useTeamMemberDetailsForSelect(activeTeamId);

  useEffect(() => {
    if (!isEmpty(teams.active)) {
      dispatch({
        type: teamActionTypes.GET_PROJECT,
        payload: { companyId: activeCompany?.companyId },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams.selectedTeamId]);

  const projectOptions = useMemo(
    () =>
      Array.isArray(teams.projects)
        ? teams.projects
            .filter((p) => !p?.data?.deleted)
            .map((p) => ({ value: p.id, label: p.data.project_name }))
        : [],
    [teams.projects]
  );

  const memberOptions = useMemo(() => {
    if (!memberDetails || typeof memberDetails !== 'object') return [];
    return Object.entries(memberDetails).map(([uid, m]) => ({
      value: uid,
      label: m?.userName || m?.userEmailid || uid,
    }));
  }, [memberDetails]);

  const [form, setForm] = useState(EMPTY_FORM);
  // CKEditor's React wrapper calls editor.setData() re-entrantly whenever its
  // `data` prop differs from the editor's current content — feeding it
  // `form.description` (which changes on every keystroke via onChange below)
  // creates a feedback loop that corrupts the editor's internal model. This
  // is set once per task load (or left empty for a new task) and never
  // updated from onChange, so `data` only ever changes when we intend to
  // reset the editor's contents wholesale.
  const [descriptionInitial, setDescriptionInitial] = useState('');
  const [loadingTask, setLoadingTask] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('comments');

  useEffect(() => {
    if (!isEditMode) return;
    setLoadingTask(true);
    database
      .collection('taskDatabase')
      .doc(taskId)
      .get()
      .then((doc) => {
        if (!doc.exists || doc.data()?.Deleted) {
          addNotification({
            message: 'This task no longer exists',
            level: notificationStates.error,
          });
          history.push(appRoutes().manageTasks);
          return;
        }
        const data = doc.data();
        setForm({
          title: data.Title || '',
          description: data.Description || '',
          assigneeId: data.Assignee || null,
          priority: data.Priority || null,
          status: data.Status || STATUS_OPTIONS[0].value,
          eta: etaToPickerDate(data.ETA),
          estimatedTime: data.EstimatedTime || '',
          projectId: data.ProjectID || null,
        });
        setDescriptionInitial(data.Description || '');
      })
      .finally(() => setLoadingTask(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, taskId]);

  const dispatchRef = useRef();
  useEffect(() => {
    dispatchRef.current = dispatch;
  }, [dispatch]);

  // Must be a plain `function`, not an arrow function — CKEditor calls
  // entries in `extraPlugins` as constructors (`new PluginFn(editor)`).
  // Arrow functions can't be constructed, so CKEditor ends up with a
  // malformed plugin instance whose `.editor` was never set, which throws
  // deep in its internals the moment anything reads `pluginInstance.editor`.
  const FileUploadPlugin = useCallback(
    function (editor) {
      return FirebaseImageUploadPlugin(
        editor,
        taskId || form.title || 'draft',
        dispatchRef
      );
    },
    [taskId, form.title]
  );

  const goToTasksList = () => history.push(appRoutes().manageTasks);

  const validate = () => {
    if (!form.title.trim()) {
      addNotification({
        message: 'Title is required',
        level: notificationStates.error,
      });
      return false;
    }
    if (!isEditMode && !form.projectId) {
      addNotification({
        message: 'Project is required',
        level: notificationStates.error,
      });
      return false;
    }
    if (!form.assigneeId) {
      addNotification({
        message: 'Assignee is required',
        level: notificationStates.error,
      });
      return false;
    }
    if (!form.priority) {
      addNotification({
        message: 'Priority is required',
        level: notificationStates.error,
      });
      return false;
    }
    if (!form.eta) {
      addNotification({
        message: 'Due date is required',
        level: notificationStates.error,
      });
      return false;
    }
    return true;
  };

  const handleCreate = async ({ clearAfter = false } = {}) => {
    if (!validate()) return;
    setSaving(true);
    const newTaskRef = database.collection('taskDatabase').doc();
    try {
      await database.runTransaction(async (transaction) => {
        const projectRef = database
          .collection('companyDatabase')
          .doc(activeCompany.companyId)
          .collection('projects')
          .doc(form.projectId);
        const projectDoc = await transaction.get(projectRef);
        const existingTaskList = projectDoc.data()?.taskList;
        const taskList = Array.isArray(existingTaskList)
          ? [...existingTaskList, newTaskRef.id]
          : [newTaskRef.id];
        transaction.update(projectRef, { taskList });
        transaction.set(newTaskRef, {
          Title: form.title.trim(),
          Description: form.description,
          Priority: form.priority,
          Assignee: form.assigneeId,
          Status: STATUS_OPTIONS[0].value,
          ProjectID: form.projectId,
          ETA: pickerDateToEta(form.eta),
          EstimatedTime: form.estimatedTime.trim(),
        });
      });
      addNotification({
        message: 'Task created successfully',
        level: notificationStates.success,
      });
      if (clearAfter) {
        setForm({ ...EMPTY_FORM, projectId: form.projectId });
        setDescriptionInitial('');
      } else {
        const project = projectOptions.find((p) => p.value === form.projectId);
        const projectSlug = (project?.label || '').split(' ').join('-');
        history.push(`/task/${projectSlug}/${newTaskRef.id}`);
      }
    } catch (error) {
      addNotification({
        message: 'Task creation failed',
        level: notificationStates.error,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await database
        .collection('taskDatabase')
        .doc(taskId)
        .update({
          Title: form.title.trim(),
          Description: form.description,
          Priority: form.priority,
          Assignee: form.assigneeId,
          Status: form.status,
          ETA: pickerDateToEta(form.eta),
          EstimatedTime: form.estimatedTime.trim(),
        });
      addNotification({
        message: 'Task updated successfully',
        level: notificationStates.success,
      });
    } catch (error) {
      addNotification({
        message: 'Task update failed',
        level: notificationStates.error,
      });
    } finally {
      setSaving(false);
    }
  };

  const priorityMeta = form.priority ? PRIORITY_ARROW[form.priority] : null;
  const priorityLeftIcon = priorityMeta ? (
    <span style={{ color: PRIORITY_ARROW_COLOR[priorityMeta.tone] }}>
      {priorityMeta.icon}
    </span>
  ) : (
    <FiFlag size={14} />
  );

  if (!isEditMode && !teams.loadingProject && projectOptions.length === 0) {
    return (
      <div className={styles.emptyPageWrap}>
        <EmptyState
          illustration
          title="No Projects Found"
          description="Create a project first to start adding tasks."
          action={
            <Button
              text="Go to Projects"
              onClick={() => history.push('/project')}
              variant={buttonVariants.primary}
            />
          }
        />
      </div>
    );
  }

  return (
    <div className={styles.taskDetailPage}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            type="button"
            className={styles.backButton}
            aria-label="Back to tasks"
            onClick={goToTasksList}
          >
            <FiArrowLeft size={18} />
          </button>
          <div>
            <Text
              variant={textVariantsEnum.Display_xs}
              fontWeight={fontWeightEnum.Bold}
            >
              {isEditMode ? form.title || 'Edit Task' : 'Create Task'}
            </Text>
            <Text variant={textVariantsEnum.Text_sm} color="gray-500">
              {isEditMode
                ? 'Update task details, track progress, and collaborate with your team.'
                : 'Add task details, set priority, and assign to team members.'}
            </Text>
          </div>
        </div>
        <div className={styles.headerActions}>
          <Button
            text="Cancel"
            variant={buttonVariants.secondary}
            onClick={goToTasksList}
            disabled={saving}
          />
          <SplitButton
            text={
              isEditMode
                ? saving
                  ? 'Saving…'
                  : 'Save Changes'
                : saving
                  ? 'Creating…'
                  : 'Create Task'
            }
            loading={saving}
            onClick={isEditMode ? handleUpdate : () => handleCreate()}
            menuItems={
              isEditMode
                ? []
                : [
                    {
                      label: 'Create & Add Another',
                      onClick: () => handleCreate({ clearAfter: true }),
                    },
                  ]
            }
          />
        </div>
      </div>

      {loadingTask ? (
        <div className={styles.pageSpinner}>
          <Spinner />
        </div>
      ) : (
        <>
          <div className={styles.body}>
            <div className={styles.card}>
              <Text
                variant={textVariantsEnum.Text_md}
                fontWeight={fontWeightEnum.Semibold}
              >
                Task Details
              </Text>
              <Input
                label="Title *"
                placeholder="Enter a short, clear title"
                value={form.title}
                onChange={(e) =>
                  setForm((f) => ({ ...f, title: e.target.value }))
                }
              />
              <div className={styles.descriptionField}>
                <Text variant={textVariantsEnum.Text_sm} color="gray-700">
                  Description
                </Text>
                <div className={styles.ckeditorWrap}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={descriptionInitial}
                    config={{
                      extraPlugins: [FileUploadPlugin],
                    }}
                    onChange={(_, editor) =>
                      setForm((f) => ({ ...f, description: editor.getData() }))
                    }
                  />
                </div>
              </div>

              <AttachmentsSection taskId={taskId} currentUser={currentUser} />
            </div>

            <div className={styles.card}>
              <Text
                variant={textVariantsEnum.Text_md}
                fontWeight={fontWeightEnum.Semibold}
              >
                Task Info
              </Text>
              {!isEditMode && (
                <Dropdown
                  label="Project *"
                  options={projectOptions}
                  value={
                    projectOptions.find((o) => o.value === form.projectId) ||
                    null
                  }
                  isLoading={teams.loadingProject}
                  selectStyles={borderedSelectStyles}
                  placeholder="Select project"
                  onChange={(opt) =>
                    setForm((f) => ({ ...f, projectId: opt?.value || null }))
                  }
                />
              )}
              <Dropdown
                label="Assignee *"
                options={memberOptions}
                value={
                  memberOptions.find((o) => o.value === form.assigneeId) || null
                }
                isLoading={membersLoading}
                selectStyles={borderedSelectStyles}
                placeholder="Select assignee"
                leftIcon={<FiUser size={15} />}
                onChange={(opt) =>
                  setForm((f) => ({ ...f, assigneeId: opt?.value || null }))
                }
              />
              <Dropdown
                label="Priority *"
                options={PRIORITY_OPTIONS}
                value={
                  PRIORITY_OPTIONS.find((o) => o.value === form.priority) ||
                  null
                }
                selectStyles={borderedSelectStyles}
                placeholder="Select priority"
                leftIcon={priorityLeftIcon}
                onChange={(opt) =>
                  setForm((f) => ({ ...f, priority: opt?.value || null }))
                }
              />
              {isEditMode && (
                <Dropdown
                  label="Status"
                  options={STATUS_OPTIONS}
                  value={
                    STATUS_OPTIONS.find((o) => o.value === form.status) || null
                  }
                  selectStyles={borderedSelectStyles}
                  placeholder="Select status"
                  onChange={(opt) =>
                    setForm((f) => ({
                      ...f,
                      status: opt?.value || STATUS_OPTIONS[0].value,
                    }))
                  }
                />
              )}
              <div className={styles.dateField}>
                <Text variant={textVariantsEnum.Text_sm} color="gray-700">
                  ETA / Due Date *
                </Text>
                <SingleDatePicker
                  date={form.eta}
                  onDateChange={(date) => setForm((f) => ({ ...f, eta: date }))}
                  disableFutureDates={false}
                  placeholder="Select due date"
                />
              </div>
              <Input
                label="Estimated Time (Optional)"
                placeholder="e.g. 4h, 2d, 30m"
                value={form.estimatedTime}
                onChange={(e) =>
                  setForm((f) => ({ ...f, estimatedTime: e.target.value }))
                }
              />
            </div>
          </div>

          {isEditMode && (
            <div className={styles.activityCard}>
              <TabBar
                variant={tabBarVariants.underline}
                tabs={[
                  {
                    value: 'comments',
                    label: 'Comments',
                    icon: <FiMessageSquare size={15} />,
                  },
                  {
                    value: 'history',
                    label: 'History',
                    icon: <FiClock size={15} />,
                  },
                ]}
                activeTab={activeTab}
                onTabClick={setActiveTab}
              />
              <div className={styles.activityContent}>
                {activeTab === 'comments' ? (
                  <CommentThread
                    taskId={taskId}
                    currentUser={currentUser}
                    memberDetails={memberDetails}
                  />
                ) : (
                  <HistoryPanel taskId={taskId} />
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TaskDetail;
